import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { EnhancedRichTextEditor } from "../../shared-components/EnhancedRichTextEditor";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import { classNames, formattedISOUTC, ISOToMilliseconds } from "../../utils";
import { usePreventLeave } from "../../utils/usePreventLeave";
import { useCreateReportTimer } from "../../api/ReportsApi";
import { ACTOR_TIMER_STATUS_ENUM } from "../../api/Reports";

export interface SummaryItem {
  body: string;
  external_id?: string;
}

export interface Reports360SummaryEditorHandle {
  discardChanges: () => void;
}

type Props = {
  reportId: string;
  workflow_run_uuid: string;
  onEditingChange?: (isEditing: boolean) => void;
  onStatusChange?: (status: string) => void;
  showStatusInline?: boolean;
};

const SUMMARY_DEFAULT_HTML = `<p><strong style="color: #2B3B72;">News/Web Review: </strong><strong style="color: #46B276;">Clear</strong> <strong style="color: #ED1C24;">Requires Review</strong></p>
<p><strong style="color: #2B3B72;">Litigation Review: </strong><strong style="color: #46B276;">Clear</strong> <strong style="color: #ED1C24;">Requires Review</strong></p>
<p><strong style="color: #2B3B72;">Social Media Review: </strong><strong style="color: #46B276;">Clear</strong> <strong style="color: #ED1C24;">Requires Review</strong></p>
<p><br></p>
<p><strong style="color: #2B3B72;">News/Web Review</strong></p>
<p><strong style="color: #4C5258;">Text</strong></p>
<p><br></p>
<p><strong style="color: #2B3B72;">Litigation Review</strong></p>
<p><strong style="color: #4C5258;">Text</strong></p>
<p><br></p>
<p><strong style="color: #2B3B72;">Social Media Review</strong></p>
<p><strong style="color: #4C5258;">Text</strong></p>`;

function normalizeSummaryBody(body?: string) {
  if (!body) return SUMMARY_DEFAULT_HTML;
  const textOnly = body
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  if (!textOnly) return SUMMARY_DEFAULT_HTML;
  return body;
}

const Reports360SummaryEditor = forwardRef<
  Reports360SummaryEditorHandle,
  Props
>(
  (
    {
      reportId,
      workflow_run_uuid,
      onEditingChange,
      onStatusChange,
      showStatusInline = true,
    },
    ref
  ) => {
    const { canEdit360Reports } = usePermissionValidator();
    const [summaryBody, setSummaryBody] =
      useState<string>(SUMMARY_DEFAULT_HTML);
    const [summaryId, setSummaryId] = useState<string>("");
    const [lastSaved, setLastSaved] = useState<string>("");
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [persistedBody, setPersistedBody] =
      useState<string>(SUMMARY_DEFAULT_HTML);
    const isEditingRef = useRef(isEditing);
    const createReportTimerMutation = useCreateReportTimer();

    const handleStartStopTimer = useCallback(() => {
      const finishedAt = formattedISOUTC(new Date().toISOString());
      const finishedAtMilliseconds = ISOToMilliseconds(finishedAt);
      const timerRequestBase = {
        name: "Summary Editor",
        value: finishedAtMilliseconds,
        workflow_run_uuid: workflow_run_uuid ?? "",
        finished_at: finishedAt,
        status: isEditing
          ? ACTOR_TIMER_STATUS_ENUM.CLOSED
          : ACTOR_TIMER_STATUS_ENUM.OPEN,
      };
      createReportTimerMutation.mutate(timerRequestBase);
    }, [isEditing, workflow_run_uuid, createReportTimerMutation]);

    const handleLeave = useCallback(() => {
      handleStartStopTimer();
      setIsEditing(false);
      setSummaryBody(persistedBody);
    }, [persistedBody, handleStartStopTimer]);

    const { LeaveWarningModal } = usePreventLeave({
      when: isEditing,
      title: "Unsaved Changes",
      message:
        "You have unsaved changes. If you leave this page, your changes will be lost.",
      onLeave: handleLeave,
    });
    const {
      data: summaryItems,
      isLoading,
      create,
      update,
    } = useCompoundResourceApi<SummaryItem>(
      `itemdal/reports/${reportId}/summaries`,
      `itemdal/summaries`
    );

    useEffect(() => {
      isEditingRef.current = isEditing;
    }, [isEditing]);

    useEffect(() => {
      if (summaryItems && summaryItems.length > 0) {
        const serverSummary = summaryItems[0];
        const serverBody = normalizeSummaryBody(serverSummary?.body);
        setPersistedBody(serverBody);
        setSummaryId(serverSummary?.external_id ?? "");
        if (isEditingRef.current) return;
        setSummaryBody(serverBody);
        return;
      }

      setPersistedBody(SUMMARY_DEFAULT_HTML);
      setSummaryId("");
      if (isEditingRef.current) return;
      setSummaryBody(SUMMARY_DEFAULT_HTML);
    }, [summaryItems]);

    useEffect(() => {
      onEditingChange?.(isEditing);
    }, [isEditing, onEditingChange]);

    useImperativeHandle(
      ref,
      () => ({
        discardChanges() {
          setSummaryBody(persistedBody);
          setIsEditing(false);
        },
      }),
      [persistedBody]
    );

    const isFetchingSummary = isLoading && !isSaving;
    const isEditDisabled = isSaving || isFetchingSummary;

    const handleToggleEdit = () => {
      if (!canEdit360Reports || isEditDisabled) return;
      handleStartStopTimer();
      if (!isEditing) {
        setIsEditing(true);

        return;
      }
      persistSummary(summaryBody || SUMMARY_DEFAULT_HTML);
    };

    function persistSummary(nextValue: string) {
      const normalizedValue = normalizeSummaryBody(nextValue);
      setIsSaving(true);
      const onSuccess = (nextSummaryId?: string) => {
        if (nextSummaryId) setSummaryId(nextSummaryId);
        setLastSaved(new Date().toLocaleString());
        setPersistedBody(normalizedValue);
        setSummaryBody(normalizedValue);
        setIsEditing(false);
        setIsSaving(false);
      };

      const onError = () => {
        setIsSaving(false);
      };

      if (summaryId) {
        updateItem(normalizedValue, onSuccess, onError);
        return;
      }

      createItem(normalizedValue, onSuccess, onError);
    }

    function createItem(
      body: string,
      onSuccess: (newSummaryId?: string) => void,
      onError: () => void
    ) {
      create.mutate(
        { body },
        {
          onSuccess: (response) => {
            const payload = response?.data ?? response;
            const createdSummaryId =
              payload?.external_id ?? payload?.id ?? payload?.data?.external_id;
            onSuccess(createdSummaryId);
          },
          onError,
        }
      );
    }

    function updateItem(
      body: string,
      onSuccess: (newSummaryId?: string) => void,
      onError: () => void
    ) {
      if (!summaryId) {
        onError();
        return;
      }
      update.mutate(
        { id: summaryId, data: { body } },
        {
          onSuccess: () => onSuccess(),
          onError,
        }
      );
    }

    const statusLabel = isFetchingSummary
      ? "Loading..."
      : isSaving
      ? "Saving..."
      : lastSaved
      ? `Last Saved: ${lastSaved}`
      : "";
    const overlayRef = useRef<HTMLDivElement>(null);
    const [toolbarHeight, setToolbarHeight] = useState<number | null>(null);

    useEffect(() => {
      const root = overlayRef.current;
      if (!root) return;

      let resizeObserver: ResizeObserver | null = null;
      let mutationObserver: MutationObserver | null = null;

      const attachResizeObserver = (toolbar: HTMLDivElement) => {
        const updateHeight = () => setToolbarHeight(toolbar.offsetHeight);
        updateHeight();
        resizeObserver = new ResizeObserver(() => updateHeight());
        resizeObserver.observe(toolbar);
      };

      const toolbar = root.querySelector<HTMLDivElement>(".ql-toolbar");
      if (toolbar) {
        attachResizeObserver(toolbar);
      } else {
        mutationObserver = new MutationObserver(() => {
          const nextToolbar = root.querySelector<HTMLDivElement>(".ql-toolbar");
          if (!nextToolbar) return;
          attachResizeObserver(nextToolbar);
          mutationObserver?.disconnect();
          mutationObserver = null;
        });
        mutationObserver.observe(root, { childList: true, subtree: true });
      }

      return () => {
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
      };
    }, []);

    useEffect(() => {
      if (!onStatusChange) return;
      onStatusChange(statusLabel);
    }, [onStatusChange, statusLabel]);

    return (
      <>
        {showStatusInline && (
          <div className="flex justify-end items-center pb-3 min-h-[1.5rem]">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {statusLabel && <span>{statusLabel}</span>}
              {isFetchingSummary && <LoadingSpinner />}
            </div>
          </div>
        )}
        <div
          ref={overlayRef}
          className="relative summary-toolbar-overlay summary-360-content"
        >
          {canEdit360Reports && (
            <div
              className="summary-toolbar-button pointer-events-none"
              style={
                toolbarHeight
                  ? {
                      height: `${toolbarHeight}px`,
                      paddingTop: "0.65rem",
                      paddingBottom: "0.65rem",
                      paddingRight: "0.65rem",
                    }
                  : {
                      paddingTop: "0.65rem",
                      paddingBottom: "0.65rem",
                      paddingRight: "0.65rem",
                    }
              }
            >
              <Button
                text={
                  isEditing
                    ? isSaving
                      ? "Saving..."
                      : "Save"
                    : isFetchingSummary
                    ? "Loading..."
                    : "Edit"
                }
                colorType={ButtonColorTypes.custom}
                type={isEditing ? ButtonTypes.default : ButtonTypes.edit}
                onClick={handleToggleEdit}
                disabled={isEditDisabled}
                className={classNames(
                  "pointer-events-auto inline-flex items-center gap-0.5 rounded px-2 py-[0.2rem] text-[10px] font-semibold leading-3 shadow-sm outline outline-[0.5px] outline-offset-[-1px] transition-colors",
                  isEditing
                    ? "bg-green-600 text-white outline-green-600 hover:bg-green-700"
                    : "bg-white text-gray-700 outline-gray-300 hover:bg-gray-50",
                  isEditDisabled ? "cursor-not-allowed opacity-70" : ""
                )}
              />
            </div>
          )}
          <EnhancedRichTextEditor
            name="summary_body"
            classes="relative w-full"
            value={summaryBody}
            handleChange={(value) => setSummaryBody(value)}
            readOnly={!isEditing || !canEdit360Reports || isSaving}
            blockImagePaste
          />
          {isFetchingSummary && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded">
              <LoadingSpinner />
            </div>
          )}
        </div>
        {LeaveWarningModal}
      </>
    );
  }
);

Reports360SummaryEditor.displayName = "Reports360SummaryEditor";

export default Reports360SummaryEditor;
